import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import UploadImageForm from "../../../components/forms/UploadImageForm";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const UnitForm = forwardRef(
  (
    { unitTypes, isLoading, isError, onDataChange, parent, initialData },
    ref
  ) => {
    // List of Payment Fequency for dropdown menu
    const paymentFrequencyObj = {
      monthly: "Monthly",
      yearly: "Yearly",
      daily: "Daily",
    };

    // List of Bedrooms for dropdown menu
    const bedroomsArray = [
      { key: "studio", value: "Studio" },
      { key: "1", value: "1 Bedroom" },
      { key: "2", value: "2 Bedrooms" },
      { key: "3", value: "3 Bedrooms" },
      { key: "4", value: "4 Bedrooms" },
      { key: "5", value: "5 Bedrooms" },
      { key: "5+", value: "5+ Bedrooms" },
    ];

    // List of Bathrooms for dropdown menu
    const bathroomsArray = [
      { key: "1", value: "1" },
      { key: "2", value: "2" },
      { key: "3", value: "3" },
      { key: "4", value: "4" },
      { key: "5", value: "5" },
      { key: "5+", value: "5+" },
    ];

    const maxLength = 100;
    // const maxLengthMsg = `This field cannot be more than ${maxLength} characters`;
    const selectErrorMsg = `Select a valid choice`;

    const [unitTypeError, setUnitTypeError] = useState(false);
    // const [unitTypeValue, setUnitTypeValue] = useState("");
    const unitTypeRef = useRef(null);

    const [bedroomsError, setBedroomsError] = useState(false);
    // const [bedroomsValue, setBedroomsValue] = useState("");
    const bedroomsRef = useRef(null);

    const [bathroomsError, setBathroomsError] = useState(false);
    // const [bathroomsValue, setBathroomsValue] = useState("");
    const bathroomsRef = useRef(null);

    const maxPriceLength = 15;
    const [priceError, setPriceError] = useState(false);
    const [priceErrorMsg, setPriceErrorMsg] = useState("");
    const priceRef = useRef(null);

    const maxSizeLength = 8;
    const [sizeError, setSizeError] = useState(false);
    const [sizeErrorMsg, setSizeErrorMsg] = useState("");
    const sizeRef = useRef(null);

    const [paymentFrequencyError, setPaymentFrequencyError] = useState(false);
    // const [paymentFrequencyValue, setPaymentFrequencyValue] = useState("");
    const paymentFrequencyRef = useRef(null);

    const [startDateError, setStartDateError] = useState(false);
    const [startDateErrorMsg, setStartDateErrorMsg] = useState("");
    const startDateRef = useRef(null);

    const [endDateError, setEndDateError] = useState(false);
    const [endDateErrorMsg, setEndDateErrorMsg] = useState("");
    const endDateRef = useRef(null);

    const handleStartDateChange = (newDate) => {
      setFormData((prev) => {
        const updatedFormData = {
          ...prev,
          startDate: newDate ? dayjs(newDate) : null,
          // Reset availability end date if it's before or equal to start date
          // endDate:
          //   prev.endDate &&
          //   (dayjs(prev.endDate).isBefore(newDate, "day") ||
          //     dayjs(prev.endDate).isSame(newDate, "day"))
          //     ? null
          //     : prev.endDate,
        };

        // Revalidate after state update
        validateDates(updatedFormData);

        // Notify parent of changes
        onDataChange(updatedFormData);

        return updatedFormData;
      });

      setStartDateError(false); // Clear error when user selects a date
      setStartDateErrorMsg("");
    };

    const handleEndDateChange = (newDate) => {
      setFormData((prev) => {
        const updatedFormData = {
          ...prev,
          endDate: newDate ? dayjs(newDate) : null,
        };

        // Revalidate after state update
        validateDates(updatedFormData);

        // Notify parent of changes
        onDataChange(updatedFormData);

        return updatedFormData;
      });
      // ({ ...prev, endDate: newDate }));
      setEndDateError(false); // Clear error when user selects a date
      setEndDateErrorMsg("");
    };

    // Validation function
    const validateDates = (formData) => {
      if (
        formData.startDate &&
        formData.endDate &&
        (formData.endDate.isBefore(formData.startDate) ||
          formData.endDate.isSame(formData.startDate))
      ) {
        setEndDateError(true);
        setEndDateErrorMsg("End date must be after start date.");
        endDateRef.current.focus();
        return false;
      } else {
        setEndDateError(false);
        return true;
      }
    };

    const maxCommentsLength = 500;
    const [commentsError, setCommentsError] = useState(false);
    const [commentsErrorMsg, setCommentsErrorMsg] = useState("");
    const commentsRef = useRef(null);

    const uploadImageFormRef = useRef();
    const [uploadImage, setUploadImage] = useState({
      // unitImages: [],
      // unitMainImage: null,

      unitImages: initialData?.unitImages || [],
      unitMainImage: initialData?.unitMainImage || null,
    });

    // Define form state for text inputs and select field
    const [formData, setFormData] = useState({
      // initialData || {
      // unitType: "",
      // size: "",
      // bedrooms: "",
      // bathrooms: "",
      // price: "",
      // paymentFrequency: "",
      // unitImages: [],
      // unitMainImage: null,
      // startDate: null,
      // endDate: null,
      // comments: "",

      unitType: initialData?.unitType || "",
      size: initialData?.size || "",
      bedrooms: initialData?.bedrooms || "",
      bathrooms: initialData?.bathrooms || "",
      price: initialData?.price || "",
      paymentFrequency: initialData?.paymentFrequency || "",
      unitMainImage: initialData?.unitMainImage || null,
      unitImages: initialData?.unitImages || [],
      startDate: initialData?.startDate ? dayjs(initialData.startDate) : null,
      endDate: initialData?.endDate ? dayjs(initialData.endDate) : null,
      comments: initialData?.comments || "",
    });

    // 1. Initial mount setup (runs once)
    useEffect(() => {
      // Run once on mount: initialize the state from initialData
      if (initialData) {
        setFormData({
          ...initialData,
          startDate: initialData.startDate
            ? dayjs(initialData.startDate)
            : null,
          endDate: initialData.endDate ? dayjs(initialData.endDate) : null,
        });

        setUploadImage({
          mainImage: formData.unitMainImage || null,
          images: formData.unitImages || [],
        });
      }
    }, []);

    useEffect(() => {
      // if the user already chose a unitType that no longer exists in the newly‐fetched list,
      // clear it so the Select falls back to “-- Select --”
      if (formData.unitType && !isLoading) {
        const hasUnitType = Object.values(unitTypes?.entities ?? {}).some(
          (ut) => ut.id === formData.unitType
        );
        if (!hasUnitType) {
          setFormData((prev) => ({ ...prev, unitType: "" }));
        }
      }
    }, [unitTypes]);

    // Handle text/select input changes & update formData state
    const handleChange = (e) => {
      const { name, value } = e.target;

      // 1. Reset unitType error to false
      if (name === "unitType") {
        // setUnitTypeValue(value);
        setUnitTypeError(false);
      }
      // 2. Reset size error to false
      if (name === "size") {
        setSizeError(false);
        setSizeErrorMsg("");
        if (value.length >= maxSizeLength) {
          setSizeErrorMsg(
            `This field cannot be more than ${maxSizeLength} characters`
          );
        }
      }
      // 2. Reset bedrooms error to false
      if (name === "bedrooms") {
        // setBedroomsValue(value);
        setBedroomsError(false);
      }
      // 3. Reset bathrooms error to false
      if (name === "bathrooms") {
        // setBathroomsValue(value);
        setBathroomsError(false);
      }
      // 4. Reset price error to false
      if (name === "price") {
        setPriceError(false);
        setPriceErrorMsg("");
        if (value.length >= maxPriceLength) {
          setPriceErrorMsg(
            `This field cannot be more than ${maxPriceLength} characters`
          );
        }
      }
      // 5. Reset paymentFrequency error to false
      if (name === "paymentFrequency") {
        // setPaymentFrequencyValue(value);
        setPaymentFrequencyError(false);
      }

      // 9. Reset comments error to false
      if (name === "comments") {
        setCommentsError(false);
        setCommentsErrorMsg("");
        if (value.length >= maxLength) {
          setCommentsErrorMsg(
            `This field cannot be more than ${maxCommentsLength} characters`
          );
        }
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        unitMainImage: prevData.unitMainImage, // Preserve unitMainImage
        unitImages: prevData.unitImages, // Preserve unitImages
      }));

      // Notify parent of data changes
      onDataChange({
        ...formData,
        [name]: value,
        unitMainImage: formData.unitMainImage, // Preserve unitMainImage
        unitImages: formData.unitImages, // Preserve unitImages
      });
    };

    const isBlank = (val) => {
      // coerce null/undefined to "", numbers to their string form, etc.
      return String(val ?? "").trim() === "";
    };

    // Validate Size field
    function validateSize(size) {
      setSizeError(false);
      setSizeErrorMsg("");

      // if (size === undefined || size === null || size.trim() === "") {
      if (isBlank(size)) {
        // size was null, undefined, empty string, or only whitespace
        setSizeError(true);
        setSizeErrorMsg("Size is required");
        return false;
      }

      if (isNaN(size)) {
        setSizeError(true);
        setSizeErrorMsg("Size must be a number");
        return false;
      }

      if (size.length > maxSizeLength) {
        setSizeError(true);
        setSizeErrorMsg(
          `This field cannot be more than ${maxSizeLength} characters`
        );
        return false;
      }

      // Convert the size to a number if it's a string
      const numericSize = parseFloat(size);

      if (numericSize < 0) {
        setSizeError(true);
        setSizeErrorMsg("Size cannot be negative");
        return false;
      }

      // Check if there are more than two decimal places
      const sizeString = size.toString();
      const decimalIndex = sizeString.indexOf(".");

      // If there is a decimal point, check that there are no more than two digits after it
      if (decimalIndex !== -1 && sizeString.length - decimalIndex - 1 > 2) {
        // return "Size can only have up to two decimal places";
        setSizeError(true);
        setSizeErrorMsg("Size can only have up to two decimal places");
        return false;
      }

      return true; // Validation passed
    }

    // Validate Price field
    function validatePrice(price) {
      setPriceError(false);
      setPriceErrorMsg("");

      // if (price === undefined || price === null || price.trim() === "") {
      if (isBlank(price)) {
        // size was null, undefined, empty string, or only whitespace
        setPriceError(true);
        setPriceErrorMsg("Price is required");
        return false;
      }

      if (isNaN(price)) {
        setPriceError(true);
        setPriceErrorMsg("Price must be a number");
        return false;
      }

      if (price.length > maxPriceLength) {
        setPriceError(true);
        setPriceErrorMsg(
          `This field cannot be more than ${maxPriceLength} characters`
        );
        return false;
      }

      // Convert the price to a number if it's a string
      const numericPrice = parseFloat(price);

      if (numericPrice < 0) {
        // return "Price cannot be negative";
        setPriceError(true);
        setPriceErrorMsg("Price cannot be negative");
        return false;
      }

      // Check if there are more than two decimal places
      const priceString = price.toString();
      const decimalIndex = priceString.indexOf(".");

      // If there is a decimal point, check that there are no more than two digits after it
      if (decimalIndex !== -1 && priceString.length - decimalIndex - 1 > 2) {
        // return "Price can only have up to two decimal places";
        setPriceError(true);
        setPriceErrorMsg("Price can only have up to two decimal places");
        return false;
      }

      return true; // Validation passed
    }

    // Validate all form fields on submit
    const validateUserInput = () => {
      // 1. Check if unitTypeValue is in unitTypes ids list
      // if (!unitTypes.ids.includes(unitTypeValue)) {

      // 1. Check if unitType is selected
      if (!formData.unitType) {
        setUnitTypeError(true);
        unitTypeRef.current.focus();
        return false;
      } else {
        setUnitTypeError(false);
      }

      // 2. Check if Size
      // is a number
      // with character length 8
      // not negative
      // and 2 decimal place
      const isValidSize = validateSize(formData.size);
      if (!isValidSize) {
        sizeRef.current.focus();
        return false;
      }

      // 2. Check if Bedrooms value is in bedroomsArray values
      // if (!bedroomsArray.some((option) => option.key === bedroomsValue)) {

      // 2. Check if Bedrooms is selected
      if (!formData.bedrooms) {
        setBedroomsError(true);
        bedroomsRef.current.focus();
        return false;
      } else {
        setBedroomsError(false);
      }

      // 3. Check if Bathrooms value is in bedroomsArray values
      // if (!bathroomsArray.some((option) => option.key === bathroomsValue)) {

      // 3. Check if Bathrooms is selected
      if (!formData.bathrooms) {
        setBathroomsError(true);
        bathroomsRef.current.focus();
        return false;
      } else {
        setBathroomsError(false);
      }

      // 4. Check if Price
      // is a number
      // with character length 15
      // not negative
      // and 2 decimal place
      const isValidPrice = validatePrice(formData.price);
      if (!isValidPrice) {
        priceRef.current.focus();
        return false;
      }

      // 5. Check if paymentFrequency value is in paymentFrequencyObj values
      // if (!Object.keys(paymentFrequencyObj).includes(paymentFrequencyValue)) {

      // 5. Check if Payment Frequency is selected
      if (!formData.paymentFrequency) {
        setPaymentFrequencyError(true);
        paymentFrequencyRef.current.focus();
        return false;
      } else {
        setPaymentFrequencyError(false);
      }

      // const isUploadImageValid = uploadImageFormRef.current.validateUserInput();
      // console.log(111, isUploadImageValid);
      // console.log(112, uploadImage);
      // console.log(112, formData.bedrooms);
      // console.log(112, formData.price);
      // console.log(112, formData.paymentFrequency);
      // console.log(113, formData);

      // 11. Check Comments Length
      if (formData.comments.length > maxCommentsLength) {
        setCommentsError(true);
        setCommentsErrorMsg(
          `This field cannot be more than ${maxCommentsLength} characters`
        );
        commentsRef.current.focus();
        return false;
      } else {
        setCommentsError(false);
        setCommentsErrorMsg("");
      }

      // Validate images
      if (!uploadImage.mainImage) {
        uploadImageFormRef.current.validateUserInput();
        return false;
      }

      if (!validateDates(formData)) return false;

      return true;
    };

    // Callback function that is triggered when UploadImageForm data changes
    const handleUploadDataChange = (data) => {
      setUploadImage(data); // Update local state

      // Update formData with new images while preserving other fields
      setFormData((prevData) => ({
        ...prevData,
        unitMainImage: data.mainImage, // Update unitMainImage
        unitImages: data.images, // Update unitImages
      }));

      // Notify parent of data changes
      onDataChange({
        ...formData,
        unitMainImage: data.mainImage, // Pass updated main image
        unitImages: data.images, // Pass updated images array
      });
    };

    // Expose the validate method to the parent via ref
    useImperativeHandle(ref, () => ({
      validateUserInput,
      // To fetch all form data from UnitForm:
      getFormData: () => ({
        ...formData,
        unitImages: uploadImage.images,
        unitMainImage: uploadImage.mainImage,
      }),
    }));

    return (
      <>
        {/* <Typography variant="h5" component="h1">
          Property Unit
        </Typography> */}

        {/* 1. Unit Type Field */}
        <FormControl
          sx={{ width: { xs: "75%", sm: 400 } }}
          error={unitTypeError}
          required
        >
          <InputLabel>Unit Type</InputLabel>
          <Select
            id="unitType"
            label="Unit Type"
            name="unitType"
            // value={formData.unitType || ""}
            value={
              // Turn the entities map into an array, then check
              Object.values(unitTypes?.entities ?? {}).some(
                (ut) => ut.id === formData.unitType
              )
                ? formData.unitType
                : ""
            }
            onChange={handleChange}
            inputRef={unitTypeRef}
            disabled={isLoading || isError}
            // endAdornment={
            //   (isLoading || isError) && (
            //     <InputAdornment position="end" sx={{ marginLeft: -11 }}>
            //       <CircularProgress size={24} />
            //     </InputAdornment>
            //   )
            // }
          >
            <MenuItem value="">-- Select --</MenuItem>
            {unitTypes?.entities ? (
              Object.values(unitTypes.entities).map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>Please select a {parent}</MenuItem>
            )}
            {/* <MenuItem value="studios">Studio</MenuItem> */}
          </Select>
          {unitTypeError && (
            <FormHelperText error>{selectErrorMsg}</FormHelperText>
          )}
        </FormControl>

        {/* 2. Size Field */}
        <TextField
          type="number"
          label="Size"
          id="size"
          name="size"
          inputRef={sizeRef}
          value={formData.size}
          onChange={handleChange}
          autoComplete="off"
          required
          sx={{ width: { xs: "75%", sm: 400 } }}
          error={sizeError} // Show error if limit is reached
          slotProps={{
            htmlInput: {
              min: 0, // Limit to 50 characters
              // step: 0.01,
              onKeyDown: (e) => {
                // Prevent "e", "E", "+", and "-" from being entered
                if (["e", "E", "+", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              },
            },
            input: {
              endAdornment: (
                <InputAdornment position="end">sq ft</InputAdornment>
              ),
            },
          }}
          helperText={
            <Typography
              variant="body4"
              sx={{ color: `${sizeError && "error"}` }}
            >
              {sizeErrorMsg}
            </Typography>
          }
        />

        {/* 2. Bedroom Field */}
        <FormControl
          sx={{ width: { xs: "75%", sm: 400 } }}
          error={bedroomsError}
          required
        >
          <InputLabel>Bedrooms</InputLabel>
          <Select
            id="bedrooms"
            label="Bedrooms"
            name="bedrooms"
            value={formData.bedrooms}
            onChange={handleChange}
            inputRef={bedroomsRef}
          >
            <MenuItem value="">-- Select --</MenuItem>
            {bedroomsArray.map(({ key, value }) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
            {/* <MenuItem value="studios">Studio</MenuItem> */}
          </Select>
          {bedroomsError && (
            <FormHelperText error>{selectErrorMsg}</FormHelperText>
          )}
        </FormControl>
        {/* JSX for dropdown with minRooms & maxRooms */}
        {/* <select
            onChange={(e) =>
              handleRoomSelection(roomOptions[e.target.selectedIndex])
            }
          >
            {roomOptions.map((option, index) => (
              <option key={index} value={option.label}>
                {option.label}
              </option>
            ))}
          </select> */}

        {/* 3. Bathroom Field */}
        <FormControl
          sx={{ width: { xs: "75%", sm: 400 } }}
          error={bathroomsError}
          required
        >
          <InputLabel>Bathrooms</InputLabel>
          <Select
            id="bathrooms"
            label="Bathrooms"
            name="bathrooms"
            value={formData.bathrooms}
            onChange={handleChange}
            inputRef={bathroomsRef}
          >
            <MenuItem value="">-- Select --</MenuItem>
            {bathroomsArray.map(({ key, value }) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
            {/* <MenuItem value="studios">Studio</MenuItem> */}
          </Select>
          {bathroomsError && (
            <FormHelperText error>{selectErrorMsg}</FormHelperText>
          )}
        </FormControl>

        {/* 4. Price Field */}
        {/* <FormControl fullWidth sx={{ width: { xs: "75%", sm: 400 } }}>
            <InputLabel htmlFor="outlined-adornment-amount">Amount</InputLabel>
            <OutlinedInput
              id="outlined-adornment-amount"
              startAdornment={
                <InputAdornment position="start">AED</InputAdornment>
              }
              label="Amount"
            />
          </FormControl> */}
        <TextField
          type="number"
          // label="Price (AED)"
          label="Price"
          id="price"
          name="price"
          inputRef={priceRef}
          value={formData.price}
          onChange={handleChange}
          autoComplete="off"
          required
          sx={{ width: { xs: "75%", sm: 400 } }}
          error={priceError} // Show error if limit is reached
          slotProps={{
            htmlInput: {
              min: 0,
              onKeyDown: (e) => {
                // Prevent "e", "E", "+", and "-" from being entered
                if (["e", "E", "+", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              },
            },
            input: {
              startAdornment: (
                <InputAdornment sx={{ mt: 0.1 }} position="start">
                  AED
                </InputAdornment>
              ),
            },
          }}
          helperText={
            <Typography
              variant="body4"
              sx={{ color: `${priceError && "error"}` }}
            >
              {priceErrorMsg}
            </Typography>
          }
        />

        {/* 5. Payment Frequency Field */}
        <FormControl
          sx={{ width: { xs: "75%", sm: 400 } }}
          error={paymentFrequencyError}
          required
        >
          <InputLabel>Payment Frequency</InputLabel>
          <Select
            id="paymentFrequency"
            label="Payment Frequency"
            name="paymentFrequency"
            value={formData.paymentFrequency}
            onChange={handleChange}
            inputRef={paymentFrequencyRef}
          >
            <MenuItem value="">-- Select --</MenuItem>
            {Object.entries(paymentFrequencyObj).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
            {/* <MenuItem value="monthlys">Monthly</MenuItem> */}
          </Select>
          {paymentFrequencyError && (
            <FormHelperText error>{selectErrorMsg}</FormHelperText>
          )}
        </FormControl>

        {/* 6. Upload Image Field */}
        <UploadImageForm
          ref={uploadImageFormRef}
          onDataChange={handleUploadDataChange}
          imageInfo={"Property interior *"}
          initialData={{
            images: initialData?.unitImages || [],
            mainImage: initialData?.unitMainImage || null,
          }}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* 7. Start Date */}
          <FormControl
            sx={{ width: { xs: "75%", sm: 400 } }}
            error={startDateError}
            required
          >
            <DatePicker
              label="Availability Start Date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleStartDateChange}
              disablePast // Prevent selecting past dates
              inputRef={startDateRef}
              // renderInput={(params) => (
              //   <TextField
              //     {...params}
              //     // required
              //     error={startDateError} // Apply error styling to the TextField
              //     helperText={startDateError ? startDateErrorMsg : ""}
              //   />
              // )}
              slotProps={{
                textField: {
                  // required: true,
                  // sx: {
                  //   width: { xs: "75%", sm: 400 },
                  //   "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                  //     {
                  //       borderColor: "red", // Red border for error state
                  //     },
                  // },
                  error: startDateError, // Apply error styling to the TextField
                },
              }}
            />
            {startDateError && (
              <FormHelperText error>{startDateErrorMsg}</FormHelperText>
            )}
          </FormControl>

          {/* 8. End Date */}
          <FormControl
            sx={{ width: { xs: "75%", sm: 400 } }}
            error={endDateError}
          >
            <DatePicker
              label="Availability End Date"
              value={formData.endDate}
              onChange={handleEndDateChange}
              disablePast
              minDate={
                formData.startDate ? formData.startDate.add(1, "day") : null
              } // Enforce at least one day after start date
              inputRef={endDateRef}
              // renderInput={(params) => (
              //   <TextField
              //     {...params}
              //     // required
              //     error={endDateError} // Apply error styling to the TextField
              //   />
              // )}
              slotProps={{
                textField: {
                  // required: true,
                  // sx: {
                  //   width: { xs: "75%", sm: 400 },
                  //   "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
                  //     {
                  //       borderColor: "red", // Red border for error state
                  //     },
                  // },
                  error: endDateError, // Apply error styling to the TextField
                },
              }}
            />
            {endDateError && (
              <FormHelperText error>{endDateErrorMsg}</FormHelperText>
            )}
          </FormControl>
        </LocalizationProvider>

        {/* <TextField
            type="text"
            label="Phone Number"
            id="phone"
            autoComplete="off"
            required
            sx={{ width: { xs: "75%", sm: 400 } }}
            slotProps={{
              input: {
                pattern: "[0-9]*", // Restricts input to numbers
              },
            }}
          /> */}

        {/* 9. Additional Information */}
        <TextField
          multiline
          minRows={5}
          maxRows={5}
          label="Additional Information"
          id="comments"
          name="comments"
          inputRef={commentsRef}
          value={formData.comments}
          onChange={handleChange}
          sx={{ width: { xs: "75%", sm: 400 } }}
          slotProps={{
            htmlInput: {
              maxLength: 500, // Limit to 50 characters
            },
          }}
          error={commentsError} // Show error if limit is reached
          helperText={
            <Typography
              variant="body4"
              sx={{ color: `${commentsError && "error"}` }}
            >
              {commentsError
                ? commentsErrorMsg
                : `${formData.comments?.length}/500`}
            </Typography>
          }
        />
        {/* <Box
        sx={{
          display: "flex",
          alignItems: "start",
          justifyContent: "flex-start",
          width: { xs: "75%", sm: 400 },
        }}
      >
        <FormControlLabel
          control={<Checkbox id="display" color="inherit" />}
          label="Display"
        />
      </Box> */}
      </>
    );
  }
);

export default UnitForm;
